import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import sweetReunionPoster from "@/assets/sweet-reunion-poster.jpeg";

const mediaItems = [
  {
    type: "image" as const,
    src: sweetReunionPoster,
    caption: "A Sweet Reunion - 26 Years Apart",
  },
  {
    type: "image" as const,
    src: "/lovable-uploads/2f914c44-8562-41e2-8cc5-8c1425968e78.png",
    caption: "Tachen's journey of hope",
  },
  {
    type: "image" as const,
    src: "/lovable-uploads/86ccd85f-c315-47ce-9963-abf9a3fad715.png",
    caption: "A story of resilience",
  },
  {
    type: "image" as const,
    src: "/lovable-uploads/af125cb6-abc4-4f47-99d4-2769126321e4.png",
    caption: "Moments that changed everything",
  },
  {
    type: "image" as const,
    src: "/lovable-uploads/c62faa4f-c54e-473a-b3c3-a5f29534de48.png",
    caption: "The miracle of connection",
  },
  {
    type: "image" as const,
    src: "/lovable-uploads/faf7974f-05aa-4352-913b-a0f628999638.png",
    caption: "26 years of hope",
  },
];

export const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  return (
    <div 
      className="relative aspect-[4/3] md:aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-black/40 backdrop-blur-sm"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          <img
            src={mediaItems[currentIndex].src}
            alt={mediaItems[currentIndex].caption}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        </motion.div>
      </AnimatePresence>

      {/* Caption */}
      <motion.div
        key={`caption-${currentIndex}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="absolute bottom-4 left-4 right-4"
      >
        <p className="text-white text-lg md:text-xl font-medium drop-shadow-lg">
          {mediaItems[currentIndex].caption}
        </p>
      </motion.div>

      {/* Navigation Arrows */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full w-10 h-10"
        onClick={goToPrevious}
      >
        <ChevronLeft className="w-6 h-6" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full w-10 h-10"
        onClick={goToNext}
      >
        <ChevronRight className="w-6 h-6" />
      </Button>

      {/* Dots Indicator */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
        {mediaItems.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? "bg-white w-6" 
                : "bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      {/* Floating badge */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute top-4 right-4 bg-story-hope text-black font-bold text-xs md:text-sm px-3 py-1.5 rounded-full shadow-lg"
      >
        🎬 EXCLUSIVE PREVIEW
      </motion.div>
    </div>
  );
};
